import { CdkDragEnter, CdkDropList, DragDropModule, moveItemInArray } from '@angular/cdk/drag-drop';
import { AfterContentChecked, AfterViewInit, ChangeDetectorRef, Component, EventEmitter, input, Input, Optional, Output, QueryList, Self, TemplateRef, ViewChild, ViewChildren } from '@angular/core';
import { MatGridListModule } from '@angular/material/grid-list';
import { CecTable, CecTableAdminStatus, CecTableStatus, CecTableType } from 'src/app/common/types/cec-table';
import { ControlBase } from '../base/control.base';
import { MatDialog } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { TranslateModule } from '@ngx-translate/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NgControl } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { CecBreakpointPipe } from '../pipes/cec-breakpoint.pipe';
import { TableManagerDialogComponent } from '../table-manager-dialog/components/table-manager-dialog.component';
import { TableManagerData } from '../table-manager-dialog/models/table-manager-data.interface';

@Component({
  selector: 'cec-table-list',
  standalone: true,
  imports: [MatGridListModule, DragDropModule, MatDividerModule, TranslateModule, MatButtonModule, MatIconModule, MatTooltipModule, CommonModule, CecBreakpointPipe, TableManagerDialogComponent],
  templateUrl: './table-list.component.html',
  styleUrl: './table-list.component.scss'
})
export class TableListComponent extends ControlBase<CecTable[]> implements AfterViewInit, AfterContentChecked {
  
  /** QueryList of drop list reference */
  @ViewChildren(CdkDropList) dropsQuery: QueryList<CdkDropList>;

  @ViewChild('dialogTemplate') addTableDialog: TemplateRef<TableManagerDialogComponent> | null = null;

  /** List of cdk drop */
  drops: CdkDropList[];

  @Input() corrected = false;

  @Input() allowPrefill = true;

  @Output() onPrefilled = new EventEmitter<void>();

  @Input() set tables(value: CecTable[]) {
    this.value = value;
    this.convertTableStatusToCecTableStatus(this.value);
    this.convertTableTypeToCecTableType(this.value);
  }

  title = input<string | undefined>(undefined);

  showWrittenLegend = input<boolean>(false);

  showCorrectLegend = input<boolean>(false);

  showCorrectedLegend = input<boolean>(false);

  constructor(@Optional() @Self() public ngControl: NgControl, private dialog: MatDialog, private cdr: ChangeDetectorRef) {
    super(ngControl);
  }

  ngAfterViewInit() {
    this.convertTableStatusToCecTableStatus(this.value);
    this.convertTableTypeToCecTableType(this.value);
    this.dropsQuery.changes.subscribe(() => {
      this.drops = this.dropsQuery.toArray();
    });
    Promise.resolve().then(() => {
      this.drops = this.dropsQuery.toArray();
    });
  }

  ngAfterContentChecked(): void {
    /** checks the differences after the table update */
    this.cdr.detectChanges();
  }

  /**
   * Handles drag and drop events in grid list
   * @param $event the drag and drop event
   */
  entered($event: CdkDragEnter): void {
    moveItemInArray(this.value, $event.item.data, $event.container.data);
  }

  /**
   * function for evaluation of cec table name based on type
   * if the type is OR, the name should be changed to both names
   * @param cecTable for evaluation of selected table
   */
  evaluateButtonName(cecTable: CecTable): string {
    if (cecTable.adminStatus === CecTableAdminStatus.OR) {
      return cecTable.content + '/' + cecTable.orTable;
    } else {
      return cecTable.content;
    }   
  }

  /**
   * function for evaluation of cec table color based on type and status
   * @param cecTable for evaluation of selected table
   */
  evaluateTableClass(cecTable: CecTable): string {
    switch (cecTable.adminStatus) {
      case CecTableAdminStatus.CANCELLED: return 'cancelled-table';
      case CecTableAdminStatus.YESORNOT: return 'yes-or-not-table';
      case CecTableAdminStatus.OR: return 'or-table';    
      default: if (cecTable.type === CecTableType.LIFESTAMP) { return 'lifestamp-table'; } else { return ''; }
    }
  }

  /**
   * function for evaluation of cec table color based on type and status
   * @param cecTable for evaluation of selected table
   */
  evaluateTableClassAfterCorrection(cecTable: CecTable): string {
    const cecTableStatus = Number(CecTableStatus[cecTable.status]);
    switch (cecTableStatus) {
      case CecTableStatus.OK: return 'ok-table';
      case CecTableStatus.MISSING: return 'missing-table';
      case CecTableStatus.MORE: return 'more-table';
      default: if (cecTable.type === CecTableType.LIFESTAMP) { return 'lifestamp-table'; } else { return ''; }
    }
  }

  convertTableTypeToCecTableType(cecTables: CecTable[]): void {
    for (const cecTable of cecTables) {
      if (cecTable.type === CecTableType.LIFESTAMP || cecTable.type.toString() === CecTableType[CecTableType.LIFESTAMP]) {
        cecTable.type = CecTableType.LIFESTAMP;
      } else {
        cecTable.type = CecTableType.NORMAL;
      }
    }
  }

  convertTableStatusToCecTableStatus(cecTables: CecTable[]): void {
    for (const cecTable of cecTables) {
      switch (cecTable.adminStatus.toString()) {
        case CecTableAdminStatus[CecTableAdminStatus.YESORNOT]: cecTable.adminStatus = CecTableAdminStatus.YESORNOT; break;
        case CecTableAdminStatus[CecTableAdminStatus.OR]: cecTable.adminStatus = CecTableAdminStatus.OR; break;
        case CecTableAdminStatus[CecTableAdminStatus.CANCELLED]: cecTable.adminStatus = CecTableAdminStatus.CANCELLED; break;
        default: cecTable.adminStatus = CecTableAdminStatus.OK; break;
      }
    }
  }

  /**
   * Opens the dialog for adding a new single table to the table list
   */
  protected openAddTableDialog(): void {
    const data: TableManagerData = { type: 'single' };
    const dialogRef = this.dialog.open(TableManagerDialogComponent, { data: data, disableClose: true });

    /** adds the table from the dialog to the table list after dialog closes */
    dialogRef.afterClosed().subscribe((result: CecTable) => {
      if (result) {
        this.onTableSaved([result]);
      }
    });
  }

  /**
   * Opens the dialog for adding a multiple new tables to the table list
   */
  protected openAddMultipleTablesDialog(): void {
    const data: TableManagerData = { type: 'multi' };
    const dialogRef = this.dialog.open(TableManagerDialogComponent, { data: data, disableClose: true });

    /** adds the multiplte tables from the dialog to the table list after dialog closes */
    dialogRef.afterClosed().subscribe((result: CecTable[]) => {
      if (result) {
        this.onTableSaved(result);
      }
    });
  }

  /**
   * Opens the dialog for edition an existing single table from the table list
   * @param cecTable for evaluation of selected table
   */
  protected openEditTableDialog(cecTable: CecTable): void {
    const data: TableManagerData = { table: cecTable, type: 'single' };
    const dialogRef = this.dialog.open(TableManagerDialogComponent, { data: data, disableClose: true });

    /** edits the table from the table list after dialog closes */
    dialogRef.afterClosed().subscribe((result: CecTable) => {
      if (result) { 
        this.value[this.value.indexOf(cecTable)] = result; 
      }
    });
  }

  /**
   * Adds a new tables to the table list
   * @param cecTables the new tables to add
   */
  protected onTableSaved(cecTables: CecTable[]): void {
    if (!this.value || !cecTables.length) {
      return;
    }

    /** pushes the created tables to the table list */
    this.value.push(...cecTables);
  }

  /**
   * Emits the event to prefill tables from correct report
   */
  protected prefillTables(): void {
    this.onPrefilled.emit();
  }

  /**
   * Clears tables from the table list
   */
  protected clearTables(): void {
    this.value = [];
  }

  /**
   * Remove table from the grid after clicking the x icon
   * @param index the index of desired table which has to be removed 
   */
  protected removeTable(index: number): void {
    this.value.splice(index, 1);
  }

  /** @inheritdoc */
  protected getControlValue(): CecTable[] {
    return this.value;
  }

  /** @inheritdoc */
  protected setControlValue(val: CecTable[]): void {
    if (!val || !val.length) {
      return null;
    }

    this.value = val;
    this.convertTableStatusToCecTableStatus(this.value);
    this.convertTableTypeToCecTableType(this.value);
  }

  /** @inheritdoc */
  protected setControlDisabled(isDisable: boolean): void {
    this.isDisabled.set(isDisable);
  }

}
